import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Download, Instagram, Twitter, Smartphone, Square, Loader2, ExternalLink, Copy, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

type CardFormat = "instagram_story" | "twitter" | "tiktok" | "square";

interface ShareCardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submissionId: number;
  gameSlug: string;
  shareToken: string;
}

const FORMAT_OPTIONS: { format: CardFormat; label: string; icon: typeof Instagram; size: string }[] = [
  { format: "instagram_story", label: "Instagram Story", icon: Instagram, size: "1080 × 1920" },
  { format: "twitter", label: "Twitter / X", icon: Twitter, size: "1200 × 675" },
  { format: "tiktok", label: "TikTok", icon: Smartphone, size: "1080 × 1920" },
  { format: "square", label: "Square (Feed)", icon: Square, size: "1080 × 1080" },
];

export default function ShareCardModal({
  open,
  onOpenChange,
  submissionId,
  gameSlug,
  shareToken,
}: ShareCardModalProps) {
  const [selectedFormat, setSelectedFormat] = useState<CardFormat>("square");
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const generateMutation = trpc.game.generateShareCardImage.useMutation();

  const shareUrl = `${window.location.origin}/game/${gameSlug}/entry/${shareToken}`;

  const handleGenerate = async (format: CardFormat) => {
    setSelectedFormat(format);
    setGeneratedUrl(null);
    try {
      const result = await generateMutation.mutateAsync({
        submissionId,
        format,
      });
      setGeneratedUrl(result.imageUrl);
      toast.success("Share card generated!");
    } catch (err) {
      toast.error("Failed to generate share card. Please try again.");
    }
  };

  const handleDownload = async () => {
    if (!generatedUrl) return;
    try {
      const response = await fetch(generatedUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gamepoint-${gameSlug}-${selectedFormat}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Share card downloaded!");
    } catch {
      // Fallback: open in new tab
      window.open(generatedUrl, "_blank");
    }
  };

  const handleCopyUrl = () => {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    toast.success("Image URL copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-background border-border/50">
        <DialogHeader>
          <DialogTitle className="text-lg">Generate Share Card</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Create a branded image card to share this submission on social media. The card includes the submission, game branding, vote count, and a QR code linking to the vote page.
          </DialogDescription>
        </DialogHeader>

        {/* Format Selection */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          {FORMAT_OPTIONS.map(({ format, label, icon: Icon, size }) => (
            <button
              key={format}
              onClick={() => handleGenerate(format)}
              disabled={generateMutation.isPending}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                selectedFormat === format && generatedUrl
                  ? "border-neon bg-neon/10"
                  : "border-border/50 hover:border-neon/30 hover:bg-secondary/50"
              }`}
            >
              <Icon className="w-5 h-5 text-neon shrink-0" />
              <div>
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">{size}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Generation Status */}
        {generateMutation.isPending && (
          <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin text-neon" />
            <span className="text-sm">Generating share card...</span>
          </div>
        )}

        {/* Preview & Download */}
        {generatedUrl && !generateMutation.isPending && (
          <div className="space-y-4 mt-2">
            <div className="rounded-lg overflow-hidden border border-border/50 bg-secondary/30">
              <img
                src={generatedUrl}
                alt="Share card preview"
                className="w-full h-auto max-h-[300px] object-contain"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleDownload}
                className="flex-1 gap-2 bg-neon hover:bg-neon/90 text-background"
              >
                <Download className="w-4 h-4" />
                Download Image
              </Button>
              <Button
                variant="outline"
                onClick={handleCopyUrl}
                className="gap-2"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                Copy URL
              </Button>
            </div>

            <div className="text-xs text-muted-foreground text-center space-y-1">
              <p>Post this image on your social media with a link to:</p>
              <Badge variant="outline" className="font-mono text-xs">
                {shareUrl}
              </Badge>
            </div>
          </div>
        )}

        {/* Instructions */}
        {!generatedUrl && !generateMutation.isPending && (
          <div className="text-center py-4 text-sm text-muted-foreground">
            <p>Select a format above to generate your share card.</p>
            <p className="text-xs mt-1">Each card includes a QR code so viewers can vote directly from the image.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
